import axios from "axios";

function resolveBackendUrl() {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "viralityai.media" || hostname.endsWith(".viralityai.media")) {
      return "https://api.viralityai.media";
    }
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:8000";
    }
    return window.location.origin;
  }
  return "http://localhost:8000";
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "virality_token";
const REFRESH_TOKEN_KEY = "virality_refresh_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
export const setRefreshToken = (t) => localStorage.setItem(REFRESH_TOKEN_KEY, t);
export const clearRefreshToken = () => localStorage.removeItem(REFRESH_TOKEN_KEY);

export const setAuthTokens = (accessToken, refreshToken) => {
  if (accessToken) setToken(accessToken);
  if (refreshToken) setRefreshToken(refreshToken);
};

export const clearAuthTokens = () => {
  clearToken();
  clearRefreshToken();
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Access Token to every outgoing request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Automatic token refresh on 401 Unauthorized
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Avoid infinite refresh loops on auth endpoints
    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/register") ||
      originalRequest.url?.includes("/auth/signup/verify") ||
      originalRequest.url?.includes("/auth/forgot-password");

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearAuthTokens();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${API}/auth/refresh`,
          { refresh_token: refreshToken },
          { withCredentials: true }
        );

        const newAccessToken = data.token;
        const newRefreshToken = data.refresh_token;

        setAuthTokens(newAccessToken, newRefreshToken);
        api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAuthTokens();
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("virality:auth:expired"));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export function apiErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : typeof e.message === "string" ? e.message : JSON.stringify(e)))
      .join(" ");
  }
  if (typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.detail === "string") return detail.detail;
    if (typeof detail.error === "string") return detail.error;
    try {
      return JSON.stringify(detail);
    } catch {
      return "Something went wrong.";
    }
  }
  return String(detail);
}

export default api;
